<template>
  <div class="project-invoices">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <template v-else>
      <!-- Invoice Form Section -->
      <div v-if="showInvoiceForm && editingInvoice" class="invoice-form-section mb-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ editingInvoice.number === '_new' ? $t('invoice_create') : $t('invoice_edit') }}
              <span v-if="editingInvoice.number !== '_new'">
                #{{ editingInvoice.number.toString().padStart(4, '0') }}
              </span>
            </h5>
            <button class="btn btn-sm btn-outline-secondary" @click="closeInvoiceForm">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="card-body">
            <InvoiceForm
              :invoice="editingInvoice"
              :projectContext="project"
              :projectNumber="project.number"
              :showCancelButton="true"
              :isReadOnly="isBlockReadOnly"
              @save="handleSaveInvoice"
              @cancel="closeInvoiceForm"
            />
          </div>
        </div>
      </div>

      <!-- Invoices Table Section -->
      <InvoicesTable
        :projectNumber="project.number"
        :tableStatePrefix="`project-${project.number}-invoices`"
        :showProjectColumn="false"
        :isReadOnly="isBlockReadOnly"
        :canOpenInvoicePage="canOpenInvoicePage"
        @edit-invoice="handleEditInvoice"
        @create-invoice="createNewInvoice"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import InvoicesTable from '@/components/Invoices/InvoicesTable.vue';
import InvoiceForm from '@/components/Invoices/InvoiceForm.vue';
import { coversAccessLevel } from '@/helpers/accessLevel';
import { INVOICES_ACCESS_NODE } from '@/navigation/accessNodes';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block project-invoices ("Rechnungen", see feature page projekt-angebote-rechnungen): the
 * invoices of the project through the paths of the block (`projectNumber` in the invoice
 * actions). The table stays usable for viewers (filters and paging); every write control is
 * covered by `isBlockReadOnly`.
 *
 * No `BlockFieldset` root: the content is a table and a form, as the view invoices.
 */
export default {
  name: 'ProjectInvoices',
  components: {
    InvoicesTable,
    InvoiceForm,
  },
  mixins: [AccessBlockMixin],
  props: {
    /** The page project; the invoices are loaded by `InvoicesTable`. */
    project: { type: Object, required: true },
  },
  data() {
    return {
      showInvoiceForm: false,
      editingInvoiceNumber: null,
    };
  },
  computed: {
    ...mapGetters(['isLoading', 'getInvoiceByNumber', 'getAccessLevel']),

    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },

    /**
     * The invoice number opens the view invoices only with a level on it; in Matrix-Authz the
     * number is plain text otherwise. Legacy-Authz: `getAccessLevel` returns editor, as today.
     */
    canOpenInvoicePage() {
      return coversAccessLevel(this.getAccessLevel(INVOICES_ACCESS_NODE), 'viewer');
    },

    // Use computed property to always get latest invoice from store
    editingInvoice() {
      if (!this.editingInvoiceNumber) return null;
      return this.getInvoiceByNumber(this.editingInvoiceNumber);
    },
  },
  methods: {
    ...mapActions(['initInvoice', 'createInvoice', 'updateInvoice', 'fetchInvoiceByNumber']),

    async handleEditInvoice(invoice) {
      await this.fetchInvoiceByNumber({
        invoiceNumber: invoice.number,
        projectNumber: this.project.number,
      });
      this.editingInvoiceNumber = invoice.number;
      this.showInvoiceForm = true;
    },

    async handleSaveInvoice(invoice) {
      try {
        if (invoice.number === '_new') {
          await this.createInvoice({ invoice, projectNumber: this.project.number });
        } else {
          await this.updateInvoice({ invoice, projectNumber: this.project.number });
        }
        this.closeInvoiceForm();
      } catch (error) {
        console.error('Error saving invoice:', error);
      }
    },

    closeInvoiceForm() {
      this.showInvoiceForm = false;
      this.editingInvoiceNumber = null;
    },

    createNewInvoice() {
      this.initInvoice();
      this.editingInvoiceNumber = '_new';
      this.showInvoiceForm = true;
    },
  },
};
</script>

<style scoped lang="scss">
.invoice-form-section {
  .card {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;

    .card-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem 1rem;

      h5 {
        color: #495057;
        font-weight: 600;
      }
    }

    .card-body {
      padding: 1rem;
    }
  }
}

.project-invoices {
  .btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;

    &:hover {
      background-color: #6c757d;
      border-color: #6c757d;
      color: white;
    }
  }
}
</style>
